<template>
  <div class="container-fluid py-4">
    <div class="d-flex align-items-center mb-3">
      <h3 class="me-3 mb-0">Transaction Report</h3>
      <small class="text-muted">
        Per provider: what we have already invoiced, and what is still to invoice, for transactions
        created in the selected date range. Repeat VINs that fall under the 6-month rule are excluded
        entirely, so every row is either booked revenue or revenue due.
      </small>
    </div>

    <form class="row g-3 align-items-end mb-3" @submit.prevent="generate">
      <div class="col-auto">
        <label for="startDate" class="form-label">Start Date</label>
        <input
          id="startDate"
          v-model="form.startDate"
          type="date"
          class="form-control"
          :max="form.endDate || undefined"
          required
        />
      </div>
      <div class="col-auto">
        <label for="endDate" class="form-label">End Date</label>
        <input
          id="endDate"
          v-model="form.endDate"
          type="date"
          class="form-control"
          :min="form.startDate || undefined"
          required
        />
      </div>
      <div class="col-auto">
        <button type="submit" class="btn btn-primary" :disabled="loading || !canSubmit">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Generate Report
        </button>
      </div>
      <div class="col-auto">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="!hasResult || exporting"
          @click="exportCsv"
        >
          <span v-if="exporting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Export to CSV
        </button>
      </div>
    </form>

    <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

    <div v-if="hasResult">
      <div class="d-flex flex-wrap align-items-center gap-3 mb-2">
        <div class="text-muted small">
          Showing {{ filteredRows.length }} row{{ filteredRows.length === 1 ? '' : 's' }} for
          {{ formatDate(result.startDate) }} – {{ formatDate(result.endDate) }}.
        </div>
        <div class="form-check form-check-inline mb-0">
          <input
            id="onlyBilled"
            v-model="filters.onlyBilled"
            class="form-check-input"
            type="checkbox"
          />
          <label class="form-check-label" for="onlyBilled">Show only billed</label>
        </div>
        <div class="form-check form-check-inline mb-0">
          <input
            id="onlyUnbilled"
            v-model="filters.onlyUnbilled"
            class="form-check-input"
            type="checkbox"
          />
          <label class="form-check-label" for="onlyUnbilled">Show only unbilled</label>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-striped table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th scope="col" class="sortable" title="Click to sort" @click="sortBy('firmName')">
                Provider Name
                <span class="sort-caret" :class="{ active: sort.key === 'firmName' }">{{ caret('firmName') }}</span>
              </th>
              <th scope="col" class="text-end sortable" title="Click to sort" @click="sortBy('isBilled')">
                Billed
                <span class="sort-caret" :class="{ active: sort.key === 'isBilled' }">{{ caret('isBilled') }}</span>
              </th>
              <th scope="col" class="text-end sortable" title="Click to sort" @click="sortBy('numTransactions')">
                # of Transactions
                <span class="sort-caret" :class="{ active: sort.key === 'numTransactions' }">{{ caret('numTransactions') }}</span>
              </th>
              <th scope="col" class="text-end sortable" title="Click to sort" @click="sortBy('totalFee')">
                Total Fees
                <span class="sort-caret" :class="{ active: sort.key === 'totalFee' }">{{ caret('totalFee') }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredRows.length === 0">
              <td colspan="4" class="text-center text-muted py-4">
                No matching transactions in the selected range.
              </td>
            </tr>
            <tr v-for="row in sortedRows" :key="`${row.firm}-${row.isBilled ? 1 : 0}`">
              <td>{{ row.firmName || '(unnamed firm)' }}</td>
              <td class="text-end">
                <span
                  class="badge"
                  :class="row.isBilled ? 'badge-billed-yes' : 'badge-billed-no'"
                  :title="
                    row.isBilled
                      ? 'Invoiced: captured by a completed billing run.'
                      : 'Not yet invoiced: due on the next billing run.'
                  "
                >
                  {{ row.isBilled ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="text-end">{{ row.numTransactions.toLocaleString() }}</td>
              <td class="text-end">{{ formatMoney(row.totalFee) }}</td>
            </tr>
          </tbody>
          <tfoot v-if="filteredRows.length > 0">
            <tr class="fw-bold table-secondary">
              <td>TOTAL</td>
              <td></td>
              <td class="text-end">{{ filteredTotals.numTransactions.toLocaleString() }}</td>
              <td class="text-end">{{ formatMoney(filteredTotals.totalFee) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '@/plugins/api'

export default {
  name: 'ProviderBillingReportView',
  data() {
    return {
      form: {
        startDate: this.firstOfThisMonth(),
        endDate: this.today()
      },
      loading: false,
      exporting: false,
      error: '',
      result: null,
      filters: {
        onlyBilled: false,
        onlyUnbilled: false
      },
      sort: {
        key: 'firmName',
        dir: 'asc'
      }
    }
  },
  computed: {
    canSubmit() {
      return !!this.form.startDate && !!this.form.endDate && this.form.endDate >= this.form.startDate
    },
    hasResult() {
      return this.result !== null
    },
    filteredRows() {
      if (!this.result) return []
      const { onlyBilled, onlyUnbilled } = this.filters
      // Both or neither checked = no restriction (show everything).
      if (onlyBilled === onlyUnbilled) return this.result.rows
      return this.result.rows.filter((r) => (onlyBilled ? r.isBilled : !r.isBilled))
    },
    filteredTotals() {
      return {
        numTransactions: this.filteredRows.reduce((sum, r) => sum + r.numTransactions, 0),
        totalFee: this.filteredRows.reduce((sum, r) => sum + Number(r.totalFee || 0), 0)
      }
    },
    sortedRows() {
      const rows = [...this.filteredRows]
      const { key, dir } = this.sort
      const mult = dir === 'asc' ? 1 : -1
      return rows.sort((a, b) => {
        if (key === 'firmName') {
          const av = (a.firmName || '').toLowerCase()
          const bv = (b.firmName || '').toLowerCase()
          return av < bv ? -mult : av > bv ? mult : 0
        }
        // isBilled (bool), numTransactions, totalFee — all compare numerically.
        const av = key === 'isBilled' ? (a.isBilled ? 1 : 0) : Number(a[key]) || 0
        const bv = key === 'isBilled' ? (b.isBilled ? 1 : 0) : Number(b[key]) || 0
        return (av - bv) * mult
      })
    }
  },
  methods: {
    today() {
      const d = new Date()
      return this.toIsoDate(d)
    },
    firstOfThisMonth() {
      const d = new Date()
      d.setDate(1)
      return this.toIsoDate(d)
    },
    toIsoDate(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatDate(iso) {
      // Accepts ISO datetimeoffset or yyyy-MM-dd; we just want the calendar day.
      return String(iso).slice(0, 10)
    },
    sortBy(key) {
      if (this.sort.key === key) {
        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.key = key
        this.sort.dir = 'asc'
      }
    },
    caret(key) {
      // Inactive columns still show a faint up/down glyph to signal they're sortable.
      if (this.sort.key !== key) return '↕'
      return this.sort.dir === 'asc' ? '▲' : '▼'
    },
    formatMoney(amount) {
      const n = Number(amount) || 0
      return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async generate() {
      this.error = ''
      this.result = null
      this.loading = true
      try {
        const data = await api.get('report/provider-billing-summary', {
          startDate: this.form.startDate,
          endDate: this.form.endDate
        })
        this.result = data
      } catch (err) {
        this.error = typeof err === 'string' ? err : 'Failed to generate report.'
      } finally {
        this.loading = false
      }
    },
    async exportCsv() {
      if (!this.result) return
      this.error = ''
      this.exporting = true
      try {
        const csv = await api.get(
          `report/provider-billing-summary?startDate=${encodeURIComponent(this.form.startDate)}&endDate=${encodeURIComponent(this.form.endDate)}&format=csv`,
          'file'
        )
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `provider-billing_${this.form.startDate}_${this.form.endDate}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (err) {
        this.error = typeof err === 'string' ? err : 'Failed to export CSV.'
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
table tfoot td {
  border-top: 2px solid #6c757d;
}

.badge-billed-yes {
  background-color: #198754; // green
  color: #fff;
}

.badge-billed-no {
  background-color: #842029; // dark red
  color: #fff;
}

th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

th.sortable:hover {
  background-color: #e9ecef;
}

th.sortable:hover .sort-caret {
  color: #495057; // darken the hint on hover to reinforce it's clickable
}

.sort-caret {
  font-size: 0.75em;
  color: #adb5bd; // faint by default so inactive columns read as "sortable"
}

.sort-caret.active {
  color: #212529; // active sort column stands out
  font-size: 0.8em;
}
</style>
